// src/features/excursions/components/AddExcursionModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import toast from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import { useClients } from "features/clients/hooks/useClients";
import { getOccupiedCamarotes } from "../hooks/useExcursionsData";
import { useCalendarRules } from "hooks/useCalendarRules";

const vehicleOptions = [
  "Van",
  "Micro",
  "Ônibus",
  "Ônibus + Micro",
  "2 Ônibus",
  "3 Ônibus",
  "4 Ônibus",
];

const camaroteOptions = ["01", "02", "03", "04"];

function AddExcursionModal({ isOpen, onClose, onAddExcursion, defaultDate }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      date: formatDateToYYYYMMDD(defaultDate),
      camaroteRequested: false,
      camaroteNumbers: [],
      status: "Agendado",
      comboIncluded: false,
    },
  });

  const { clients, fetchClients, addClient } = useClients();
  const [filteredClients, setFilteredClients] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  const [occupiedCamarotes, setOccupiedCamarotes] = useState([]);
  const [isCheckingCamarotes, setIsCheckingCamarotes] = useState(false);

  const camaroteRequested = watch("camaroteRequested");
  const selectedDateString = watch("date");
  const clientNameValue = watch("clientName");
  const clientPhoneValue = watch("clientPhone");

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen, fetchClients]);

  useEffect(() => {
    let searchTerm = "";
    let shouldSearch = false;

    if (showNameSuggestions && clientNameValue?.length > 1) {
      searchTerm = clientNameValue;
      shouldSearch = true;
    } else if (showPhoneSuggestions && clientPhoneValue?.length > 1) {
      searchTerm = clientPhoneValue;
      shouldSearch = true;
    }

    if (shouldSearch) {
      const lowerTerm = searchTerm.toLowerCase();
      const matches = clients.filter(
        (c) =>
          c.name.toLowerCase().includes(lowerTerm) ||
          c.phone.includes(lowerTerm) ||
          (c.cpf && c.cpf.includes(lowerTerm)),
      );
      setFilteredClients(matches.slice(0, 5));
    } else {
      setFilteredClients([]);
    }
  }, [
    clientNameValue,
    clientPhoneValue,
    showNameSuggestions,
    showPhoneSuggestions,
    clients,
  ]);

  const handleSelectClient = (client) => {
    setValue("clientName", client.name);
    setValue("clientPhone", client.phone);
    setValue("city", client.city || "");
    setShowNameSuggestions(false);
    setShowPhoneSuggestions(false);
  };

  const holidayName = useMemo(() => {
    if (!selectedDateString) return null;
    const [year, month, day] = selectedDateString.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day);
    return checkHoliday(dateObj);
  }, [selectedDateString]);

  useEffect(() => {
    const fetchCamarotes = async () => {
      if (isOpen && selectedDateString) {
        setIsCheckingCamarotes(true);
        const [year, month, day] = selectedDateString.split("-").map(Number);
        const dateObj = new Date(year, month - 1, day);

        const occupied = await getOccupiedCamarotes(dateObj);
        setOccupiedCamarotes(occupied);
        setIsCheckingCamarotes(false);
      }
    };
    fetchCamarotes();
  }, [selectedDateString, isOpen]);

  useEffect(() => {
    if (isOpen) {
      reset({
        date: formatDateToYYYYMMDD(defaultDate),
        clientName: "",
        clientPhone: "",
        city: "",
        quantity: "",
        status: "Agendado",
        vehicle: "",
        camaroteRequested: false,
        camaroteNumbers: [],
        comboIncluded: false,
      });
      setShowNameSuggestions(false);
      setShowPhoneSuggestions(false);
    }
  }, [isOpen, defaultDate, reset]);

  const { checkDateAvailability, isLoading: isLoadingRules } =
    useCalendarRules();

  const onSubmit = async (data) => {
    if (!data.clientName || !data.date) {
      toast.error("Data e Nome do Cliente são obrigatórios.");
      return;
    }

    if (isLoadingRules) {
      toast.error("Aguarde, verificando disponibilidade do calendário...");
      return;
    }

    // Convert date string to Date object without timezone issues
    const [year, month, day] = data.date.split("-").map(Number);
    const dateObj = new Date(year, month - 1, day);

    const isDateOpen = checkDateAvailability(dateObj);

    if (!isDateOpen) {
      toast.error(`O Parque está fechado na data selecionada (${data.date}).`);
      return;
    }

    if (
      data.camaroteRequested &&
      (!data.camaroteNumbers || data.camaroteNumbers.length === 0)
    ) {
      toast.error("Selecione pelo menos um número de camarote.");
      return;
    }

    await addClient({
      name: data.clientName,
      phone: data.clientPhone,
      city: data.city,
      source: "excursion_modal",
    });

    onAddExcursion(data);
    onClose();
  };

  const SuggestionsList = () => (
    <ul className="absolute z-50 w-full bg-white dark:bg-slate-900 border rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto left-0">
      {filteredClients.map((client) => (
        <li
          key={client.id}
          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer text-sm border-b last:border-0"
          onClick={() => handleSelectClient(client)}
        >
          <div className="font-medium">{client.name}</div>
          <div className="text-xs text-muted-foreground flex justify-between">
            <span>{client.phone}</span>
            <span>{client.city}</span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Nova Excursão</DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 max-h-[80vh] overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* -- SEÇÃO: DATA -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Data
              </h3>
              <div className="flex flex-col w-fit space-y-1">
                <Label htmlFor="date">Data da Excursão</Label>
                <Input
                  id="date"
                  type="date"
                  {...register("date", { required: true })}
                  className="force-light-datepicker w-full min-w-[160px]"
                />
                {holidayName && (
                  <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                    <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                    {holidayName}
                  </div>
                )}
              </div>
            </div>

            {/* -- SEÇÃO: CLIENTE -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Cliente
              </h3>
              <div className="space-y-4">
                <div className="space-y-1 relative">
                  <Label htmlFor="clientName">Nome Completo</Label>
                  <Input
                    id="clientName"
                    {...register("clientName", {
                      required: "Nome é obrigatório",
                    })}
                    onFocus={() => {
                      setShowNameSuggestions(true);
                      setShowPhoneSuggestions(false);
                    }}
                    autoComplete="off"
                    placeholder="Buscar cliente..."
                  />
                  {showNameSuggestions && filteredClients.length > 0 && (
                    <SuggestionsList />
                  )}
                  {errors.clientName && (
                    <p className="text-xs text-destructive">
                      {errors.clientName.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1 relative">
                    <Label htmlFor="clientPhone">Telefone</Label>
                    <Input
                      id="clientPhone"
                      {...register("clientPhone")}
                      onFocus={() => {
                        setShowPhoneSuggestions(true);
                        setShowNameSuggestions(false);
                      }}
                      autoComplete="off"
                      placeholder="(99) 99999-9999"
                    />
                    {showPhoneSuggestions && filteredClients.length > 0 && (
                      <SuggestionsList />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="city">Cidade/Origem</Label>
                    <Input
                      id="city"
                      {...register("city")}
                      placeholder="Ex: Aracaju"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: DETALHES -- */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Detalhes
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="quantity">Quantidade</Label>
                  <Input
                    id="quantity"
                    type="number"
                    {...register("quantity", { valueAsNumber: true })}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vehicle">Veículo</Label>
                  <Controller
                    name="vehicle"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue=""
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Opções (Pacote e Camarote) */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center space-x-2 border p-3 rounded-md">
                  <Controller
                    name="comboIncluded"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="comboIncluded"
                      />
                    )}
                  />
                  <label
                    htmlFor="comboIncluded"
                    className="text-sm font-medium leading-none cursor-pointer flex-grow"
                  >
                    Combo (Entrada+Almoço)
                  </label>
                </div>

                <div className="border rounded-md p-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Controller
                      name="camaroteRequested"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(val) => {
                            field.onChange(val);
                            if (!val) setValue("camaroteNumbers", []);
                          }}
                          id="camaroteRequested"
                        />
                      )}
                    />
                    <label
                      htmlFor="camaroteRequested"
                      className="text-sm font-medium leading-none cursor-pointer flex-grow"
                    >
                      Camarote
                    </label>
                  </div>

                  {camaroteRequested && (
                    <div className="animate-in fade-in slide-in-from-top-1 pt-1">
                      <Label className="text-xs text-muted-foreground mb-2 flex justify-between">
                        Selecione o número:
                        {isCheckingCamarotes && (
                          <span className="italic">Verificando...</span>
                        )}
                      </Label>
                      <Controller
                        name="camaroteNumbers"
                        control={control}
                        render={({ field }) => (
                          <div className="grid grid-cols-4 gap-2">
                            {camaroteOptions.map((num) => {
                              const currentSelection = field.value || [];
                              const isSelected = currentSelection.includes(num);
                              const isOccupied =
                                occupiedCamarotes.includes(num);

                              return (
                                <button
                                  key={num}
                                  type="button"
                                  disabled={isOccupied}
                                  onClick={() => {
                                    if (isSelected) {
                                      field.onChange(
                                        currentSelection.filter(
                                          (n) => n !== num,
                                        ),
                                      );
                                    } else {
                                      if (currentSelection.length < 2) {
                                        field.onChange([
                                          ...currentSelection,
                                          num,
                                        ]);
                                      } else {
                                        toast.error(
                                          "Máximo de 2 camarotes por reserva.",
                                        );
                                      }
                                    }
                                  }}
                                  className={`
                                    h-10 rounded-md text-sm font-medium transition-colors border
                                    ${
                                      isOccupied
                                        ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed border-transparent decoration-slice line-through"
                                        : isSelected
                                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                          : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                                    }
                                  `}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 sticky bottom-0 bg-background">
              <Button type="submit" size="lg" className="w-full sm:w-auto">
                Adicionar
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddExcursionModal;
